import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointUp,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment, useState } from 'react'
import { useDispatch } from 'react-redux'
import SvgTooltip from 'src/components/Svgs/SvgTooltip'
import SvgTooltipHoverColored from 'src/components/Tooltip/SvgTooltipHoverColored'
import TooltipBody, {
  TooltipBodyDesigns,
} from 'src/components/TooltipBody/TooltipBody'
import { createSetIsTooltipPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface TooltipProps {
  className?: string
  openLeft: boolean
  openDown?: boolean
  hideMdUp?: boolean
  hideSmDown?: boolean
  iconSrc?: React.ReactElement | string
  isAbsolute?: boolean
  showToolTip?: boolean
  tooltipColor?: string
  transformScale?: number
  transformOrigin?: string
  maxWidth?: number
  width?: number
  alwaysOpen?: boolean // for debugging purposes
  onHover?: () => void
  zIndex?: string
  iconTestId?: string
}

const Tooltip: React.FC<React.PropsWithChildren<TooltipProps>> = (
  props,
): React.ReactElement => {
  // when the tooltip is getting cutoff make sure to set overflow: visible on the parent element
  const [isHover, setIsHover] = useState<boolean>(false)

  const dispatch = useDispatch()

  const iconBaseStyle = css`
    cursor: pointer;

    ${getBreakpointUp(breakpointSizes.SM)(css`
      ${!!props.showToolTip &&
      css`
        display: none;
      `}

      ${isHover &&
      css`
        display: none;
      `}
    `)}
  `

  const iconHoverStyle = css`
    cursor: pointer;
    display: none;

    ${getBreakpointUp(breakpointSizes.SM)(css`
      ${!!props.showToolTip &&
      css`
        display: inline;
      `}

      ${isHover &&
      css`
        display: inline;
      `}
    `)}
  `

  return (
    <div
      className={props.className}
      css={css`
        position: relative;
        color: ${themeConstants.colors.cta};
        display: flex;
        align-items: center;
        z-index: ${props.zIndex ?? '1'};

        ${!!props.hideMdUp &&
        css`
          ${getBreakpointUp(breakpointSizes.MD)(css`
            display: none;
          `)}
        `}

        ${!!props.hideSmDown &&
        css`
          ${getBreakpointDown(breakpointSizes.SM)(css`
            display: none;
          `)}
        `}

        ${!!props.isAbsolute &&
        css`
          position: absolute;
        `}
      `}
      onMouseEnter={(): void => {
        if (props.onHover !== undefined) {
          props.onHover()
        }
        setIsHover(true)
      }}
      onMouseLeave={(): void => setIsHover(false)}
      data-testid={props.iconTestId}
    >
      {props.iconSrc === undefined && (
        <Fragment>
          <SvgTooltip
            fill={props?.tooltipColor}
            css={css`
              ${iconBaseStyle}
              transform: scale(${props.transformScale || 0.8});
              transform-origin: ${props.transformOrigin || css`center`};
            `}
            onClick={(): void => {
              dispatch(createSetIsTooltipPopupOpen(true, props.children))
            }}
          />
          <SvgTooltipHoverColored
            fill={props?.tooltipColor}
            css={css`
              ${iconHoverStyle}
            `}
          />
        </Fragment>
      )}
      {props.iconSrc !== undefined &&
        (typeof props.iconSrc === 'string' ? (
          <Fragment>
            <img
              src={props.iconSrc}
              alt={''}
              css={css`
                ${iconBaseStyle}
                width: 16px;
              `}
            />
            <img
              src={props.iconSrc}
              alt={''}
              css={css`
                ${iconHoverStyle}
                width: 16px;
              `}
            />
          </Fragment>
        ) : (
          props.iconSrc
        ))}
      <div
        css={css`
          display: none;
          position: absolute;
          pointer-events: none;
          height: 100%;
          width: 15%;

          ${getBreakpointUp(breakpointSizes.SM)(css`
            ${!!props.showToolTip &&
            css`
              display: inline;
              pointer-events: all;
              left: 100%;
            `}

            ${isHover &&
            css`
              display: inline;
              pointer-events: all;

              ${props.openLeft &&
              css`
                right: 100%;
              `}
              ${!props.openLeft &&
              css`
                left: 100%;
              `}
            `}
          `)}
        `}
      />
      <TooltipBody
        css={css`
          position: absolute;
          pointer-events: none;
          opacity: 0;
          transition:
            opacity 150ms ease-out,
            left 150ms ease-out,
            right 150ms ease-out,
            top 150ms ease-out;
          top: 0px;
          overflow: visible;
          ${props.maxWidth &&
          css`
            max-width: ${props.maxWidth}px;
          `}
          width: ${props.width || 220}px;
          border-top-right-radius: ${styleConstants.borderRadius.tooltip};
          border-bottom-right-radius: ${styleConstants.borderRadius.tooltip};

          ${!props.openDown &&
          css`
            ${props.openLeft &&
            css`
              right: 100%;
            `}
            ${!props.openLeft &&
            css`
              left: 100%;
            `}
          `}
          ${props.openDown &&
          css`
            top: 100%;

            ${props.openLeft &&
            css`
              right: 0%;
            `}
            ${!props.openLeft &&
            css`
              left: 0%;
            `}
          `}

          ${getBreakpointUp(breakpointSizes.SM)(css`
            ${!!props.showToolTip &&
            css`
              pointer-events: all;
              opacity: 1;
              left: 115%;
              z-index: 4;
              background-color: ${styleConstants.colors.white};
            `}

            ${(isHover || props.alwaysOpen) &&
            css`
              pointer-events: all;
              opacity: 1;
              z-index: 4;
              background-color: ${styleConstants.colors.white};

              ${!props.openDown &&
              css`
                ${props.openLeft &&
                css`
                  right: 115%;
                `}
                ${!props.openLeft &&
                css`
                  left: 115%;
                `}
              `}
              ${props.openDown &&
              css`
                top: 115%;

                ${props.openLeft &&
                css`
                  right: 0%;
                `}
                ${!props.openLeft &&
                css`
                  left: 0%;
                `}
              `}
            `}
          `)}
        `}
        design={TooltipBodyDesigns.TooltipHover}
        hideXsmDown
      >
        {props.children}
      </TooltipBody>
    </div>
  )
}

export default Tooltip
